import { Fragment } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ContentWrapper from '~/tutor-on-demand/components/Content/ContentWrapper'
import HeaderTitle from '~/tutor-on-demand/components/Header/HeaderTitle'
import HeaderWrapper from '~/tutor-on-demand/components/Header/HeaderWrapper'
import List from '~/tutor-on-demand/components/Typography/List'
import TutorOnDemandProductCards from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.component'
import Paragraph from '~/tutor-on-demand/components/Typography/Paragraph'
import SubContainer from '~/tutor-on-demand/components/Content/SubContainer'
import SubContainerWrapper from '~/tutor-on-demand/components/Content/SubContainerWrapper'
import { SubjectType, getSubjectImage } from '~/components/Metadata/SubjectMetatags'

import { Breakpoint, useCheckBreakpoint } from '~/utils'
import ROUTES from '~/shared/constants/routes.const'
import BreadCrumbs from '~/encyclopedia/components/BreadCrumbs'

enum Topic {
  ANATOMY = 'anatomy',
  PHYSIOLOGY = 'physiology',
  CELLULAR_BIOLOGY = 'cellular-biology',
  BIOCHEMISTRY = 'biochemistry',
  GENETICS = 'genetics',
  MOLECULAR_BIOLOGY = 'molecular-biology',
  MICROBIOLOGY = 'microbiology',
  BIOTECHNOLOGY = 'biotechnology',
  ECOLOGY = 'ecology',
}

const biologyLevels = ['Biologi 1/REA 3001', 'Biologi 2/REA 3002 og REA 3003']

export default function TutorOnDemandBiology({ type }: { type: SubjectType }): React.ReactElement {
  const { t } = useTranslation(['tutorOnDemand', 'tutorOnDemandBiology'])
  const breadcrumbs = [
    { path: ROUTES.TUTOR_ON_DEMAND_INFORMATION, h1: t('tutorOnDemand:landingPage.whatIsTutorOnDemand.title') },
    { path: ROUTES.TUTOR_ON_DEMAND_BIOLOGY, h1: t('tutorOnDemandBiology:title') },
  ]
  const { locale } = useRouter()
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.XL })

  return (
    <Fragment>
      <HeaderWrapper>
        <BreadCrumbs
          breadcrumbs={breadcrumbs}
          fontSize="medium"
          isWhite
          itemClassNames="font-medium items-center"
          wrapperClassNames="mt-1 pr-12 lg:pr-0"
        />

        <HeaderTitle
          iconAlt={getSubjectImage(type, t).alt as string}
          iconSrc={getSubjectImage(type, t).srcKey as string}
        >
          {t('tutorOnDemandBiology:title')}
        </HeaderTitle>
      </HeaderWrapper>
      <ContentWrapper className="space-y-8">
        <div className="xl:space-x-8 xl:flex">
          <section className="mb-6 space-y-6 xl:mb-0">
            <Paragraph>{t('tutorOnDemandBiology:contentParagraph1')}</Paragraph>
            <Paragraph>{t('tutorOnDemandBiology:contentParagraph2')}</Paragraph>
            <Paragraph>{t('tutorOnDemandBiology:contentParagraph3')}</Paragraph>
            {!isSmallScreen && (
              <TutorOnDemandProductCards
                className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
                colorScheme="white"
                hideChatWithTutor
                hideVideoChatWithTutor
              />
            )}
          </section>
          <SubContainerWrapper>
            <SubContainer className="w-full max-w-sm" header={t('tutorOnDemandBiology:weCanHelpYouWith.header')}>
              <List
                items={Object.values(Topic).map((item) => t(`tutorOnDemandBiology:weCanHelpYouWith.items.${item}`))}
              />
            </SubContainer>
            {locale === 'no' && (
              <SubContainer className="w-full max-w-sm" header="Vi kan alle fag i biologi, som for eksempel">
                <List items={biologyLevels} />
              </SubContainer>
            )}
          </SubContainerWrapper>
        </div>
        {isSmallScreen && (
          <TutorOnDemandProductCards
            className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
            colorScheme="white"
            hideChatWithTutor
            hideVideoChatWithTutor
          />
        )}
      </ContentWrapper>
    </Fragment>
  )
}
